/**
 * Permissions utility for role-based access control
 */

// Platform-level role values (stored on user.platform_role)
export const PLATFORM_ROLES = {
  SYSTEM_ADMIN: 'system_admin',
  ACCOUNT_MANAGER: 'account_manager',
};

// Agency-level role constants (stored on agency membership)
export const ROLES = {
  AGENT: 'Agent',
  RENTER: 'Renter',
  OWNER: 'Owner',
};

// Permission constants
export const PERMISSIONS = {
  // Users
  USERS_VIEW: 'users.view',
  USERS_MANAGE: 'users.manage',

  // Agencies
  AGENCIES_VIEW: 'agencies.view',
  AGENCIES_MANAGE: 'agencies.manage',

  // Properties
  PROPERTIES_VIEW: 'properties.view',
  PROPERTIES_MANAGE: 'properties.manage',

  // Rooms
  ROOMS_VIEW: 'rooms.view',
  ROOMS_MANAGE: 'rooms.manage',

  // Leases
  LEASES_VIEW: 'leases.view',
  LEASES_MANAGE: 'leases.manage',

  // Payments
  PAYMENTS_VIEW: 'payments.view',
  PAYMENTS_MANAGE: 'payments.manage',
  PAYMENTS_VERIFY: 'payment.verification',

  // Expenses
  EXPENSES_VIEW: 'expenses.view',
  EXPENSES_MANAGE: 'expenses.manage',

  // Ledgers
  LEDGERS_VIEW: 'ledgers.view',
  LEDGERS_MANAGE: 'ledgers.manage',

  // Payouts
  PAYOUTS_VIEW: 'payouts.view',
  PAYOUTS_MANAGE: 'payouts.manage',

  // Reports
  REPORTS_VIEW: 'reports.view',

  // Roles
  ROLES_VIEW: 'roles.view',
  ROLES_MANAGE: 'roles.manage',

  // System
  SYSTEM_SETTINGS: 'system.settings',
};

/**
 * Check if user has a specific role
 * @param {Object} user - User object with roles array
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return user.roles.includes(role);
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - User object with roles array
 * @param {string[]} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return roles.some(role => user.roles.includes(role));
};

/**
 * Check if user has all of the specified roles
 * @param {Object} user - User object with roles array
 * @param {string[]} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAllRoles = (user, roles) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return roles.every(role => user.roles.includes(role));
};

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with permissions array
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.permissions || !Array.isArray(user.permissions)) {
    return false;
  }
  return user.permissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object with permissions array
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user || !user.permissions || !Array.isArray(user.permissions)) {
    return false;
  }
  return permissions.some(permission => user.permissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - User object with permissions array
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user || !user.permissions || !Array.isArray(user.permissions)) {
    return false;
  }
  return permissions.every(permission => user.permissions.includes(permission));
};

/**
 * Check if user is a platform-level system admin (user.platform_role === 'system_admin')
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isPlatformAdmin = (user) => {
  return user?.platform_role === PLATFORM_ROLES.SYSTEM_ADMIN;
};

/**
 * Check if user is a platform-level account manager
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isAccountManager = (user) => {
  return user?.platform_role === PLATFORM_ROLES.ACCOUNT_MANAGER;
};

/**
 * Check if user has any platform role (i.e. is platform staff, not an agency user)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isPlatformUser = (user) => {
  return !!user?.platform_role;
};

/**
 * Check if user is an Agent (agency membership role)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isAgent = (user) => {
  return hasRole(user, ROLES.AGENT);
};

/**
 * Check if user can access agent features (Agent, or any platform user, or custom role
 * with property management permissions)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canAccessAgentFeatures = (user) => {
  if (isPlatformUser(user) || isAgent(user)) return true;
  // Custom roles with property management access (e.g. "Agent Manager")
  return hasPermission(user, PERMISSIONS.PROPERTIES_MANAGE);
};

/**
 * Check if user can access admin features (platform admins only)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const canAccessAdminFeatures = (user) => {
  return isPlatformAdmin(user);
};

/**
 * Get navigation items based on user permissions
 * @param {Object} user - User object
 * @returns {Object} - Object with accessible navigation items
 */
export const getAccessibleNavigation = (user) => {
  return {
    // Agent features - accessible by agency agents and all platform users
    properties: canAccessAgentFeatures(user),
    collections: canAccessAgentFeatures(user),
    occupants: canAccessAgentFeatures(user),

    // Admin features - platform admins only
    agencies: canAccessAdminFeatures(user),
    admin: canAccessAdminFeatures(user),
    userManagement: canAccessAdminFeatures(user),
    roleManagement: canAccessAdminFeatures(user),
  };
};
